import { useCallback, useEffect, useMemo } from "react"
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { NotFoundError } from "api/errors"
import { useAppDispatch } from "hooks/redux"
import { useAccountAlias } from "resources/account"
import { useScheduledWorkflowDeliverable } from "resources/scheduled-workflows/hooks"
import { buildScheduledWorkflowFileUrl } from "resources/scheduled-workflows/routes"
import type { ScheduledWorkflowFileUrlKind } from "resources/scheduled-workflows/routes"
import type { ScheduledWorkflowDeliverableFile } from "resources/scheduled-workflows/types"
import { closeSkillsPane, setSurfaceView } from "../store/agent-chat-slice"
import type { ArtifactEnvelope, FileArtifact } from "../chatbox/artifact-contract"
import { ArtifactPanelBody } from "../chatbox/artifact-renderers"
import { useArtifactDownload } from "../chatbox/use-artifact-download"
import { primaryFileOf } from "./report-file"
import { reportEntryLabel, reportSourceLine } from "./report-format"
import { viewerTimeZone } from "./schedule-format"

const CONTEXT_SX = { display: "flex", alignItems: "center", gap: 1, pb: 1.5 } satisfies SxProps<Theme>
const SOURCE_SX = { flex: 1, minWidth: 0, color: "text.secondary" } satisfies SxProps<Theme>
const WORKFLOW_NAME_SX = { color: "text.primary" } satisfies SxProps<Theme>
const FILE_DOWNLOAD_SX = { p: 0, minWidth: 0, textTransform: "none", fontWeight: "normal" } satisfies SxProps<Theme>
const OTHER_FILES_SX = { display: "flex", flexDirection: "column", gap: 0.5, pt: 1.5, m: 0, pl: 0, listStyle: "none" } satisfies SxProps<Theme>

function reportFileArtifact(deliverableId: string, file: ScheduledWorkflowDeliverableFile): FileArtifact {
  return { id: deliverableId, type: "file", filename: file.filename, contentType: file.contentType, deliverable: { deliverableId, fileId: file.fileId } }
}

export const REPORT_PANEL_NAME = "Report"

export function reportArtifactOf(deliverableId: string): ArtifactEnvelope {
  // no filename: there is no file to name, so isFileArtifact keeps this out of the file renderer
  return { id: deliverableId, type: "file", title: REPORT_PANEL_NAME, deliverable: { deliverableId } }
}

function useReportFileSource(deliverableId: string, file: ScheduledWorkflowDeliverableFile) {
  const accountAlias = useAccountAlias()
  const artifact = useMemo(() => reportFileArtifact(deliverableId, file), [deliverableId, file])
  const urlFor = useCallback(
    (kind: ScheduledWorkflowFileUrlKind) => buildScheduledWorkflowFileUrl({ accountAlias, deliverableId, fileId: file.fileId }, kind),
    [accountAlias, deliverableId, file.fileId],
  )
  return { artifact, urlFor }
}

interface ReportFileProps {
  deliverableId: string
  file: ScheduledWorkflowDeliverableFile
  isExpanded: boolean
}

function ReportFile({ deliverableId, file, isExpanded }: ReportFileProps) {
  const { artifact, urlFor } = useReportFileSource(deliverableId, file)
  return <ArtifactPanelBody artifact={artifact} isExpanded={isExpanded} threadId={null} urlFor={urlFor} />
}

interface ReportFileDownloadProps {
  deliverableId: string
  file: ScheduledWorkflowDeliverableFile
}

function ReportFileDownload({ deliverableId, file }: ReportFileDownloadProps) {
  const { artifact, urlFor } = useReportFileSource(deliverableId, file)
  const { downloadArtifact, isDownloading } = useArtifactDownload(artifact, null, null, urlFor)
  const handleDownload = useCallback(() => downloadArtifact(null, deliverableId), [downloadArtifact, deliverableId])
  return (
    <Button variant="text" size="small" aria-label={`Download ${file.filename}`} disabled={isDownloading} onClick={handleDownload} sx={FILE_DOWNLOAD_SX}>
      {file.filename}
    </Button>
  )
}

interface OtherFilesProps {
  deliverableId: string
  files: ScheduledWorkflowDeliverableFile[]
}

function OtherFiles({ deliverableId, files }: OtherFilesProps) {
  if (files.length === 0) return null
  return (
    <Box component="ul" sx={OTHER_FILES_SX}>
      {files.map((file) => (
        <li key={file.fileId}><ReportFileDownload deliverableId={deliverableId} file={file} /></li>
      ))}
    </Box>
  )
}

interface ReportPanelBodyProps {
  deliverableId: string
  fileId?: string
  isExpanded: boolean
  onClose: () => void
  onName: (name: { deliverableId: string, text: string }) => void
}

export function ReportPanelBody({ deliverableId, fileId, isExpanded, onClose, onName }: ReportPanelBodyProps) {
  const dispatch = useAppDispatch()
  const { data: report, isLoading, error, refetch } = useScheduledWorkflowDeliverable(deliverableId)
  const file = report == null ? undefined : report.files.find((candidate) => candidate.fileId === fileId) ?? primaryFileOf(report.files)
  const openWorkflow = useCallback(() => {
    dispatch(closeSkillsPane())
    dispatch(setSurfaceView("workflows"))
    onClose()
  }, [dispatch, onClose])

  useEffect(() => {
    if (report != null) onName({ deliverableId, text: reportEntryLabel(report, viewerTimeZone()) })
  }, [report, deliverableId, onName])

  if (isLoading) return <CircularProgress size={24} aria-label="Loading report" />
  // no cached report to fall back on: a failed background refetch keeps showing the cached one
  if (report == null && error != null && !(error instanceof NotFoundError)) {
    return (
      <Alert severity="error" action={<Button size="small" onClick={() => refetch()}>Try again</Button>}>Couldn&apos;t load this report. Please try again.</Alert>
    )
  }
  if (report == null || file == null) return <Alert severity="error">This report is no longer available.</Alert>

  const otherFiles = report.files.filter((candidate) => candidate.fileId !== file.fileId)
  const source = reportSourceLine(report.workflowName, report.workflowSchedule, viewerTimeZone(), new Date())
  return (
    <>
      <Box sx={CONTEXT_SX}>
        <Typography variant="body2" sx={SOURCE_SX}>
          {source.before}<Box component="strong" sx={WORKFLOW_NAME_SX}>{source.workflowName}</Box>{source.after}
        </Typography>
        <Button size="small" variant="outlined" onClick={openWorkflow}>Open workflow</Button>
      </Box>
      <ReportFile deliverableId={deliverableId} file={file} isExpanded={isExpanded} />
      <OtherFiles deliverableId={deliverableId} files={otherFiles} />
    </>
  )
}
